/**
 * How an excerpt looks where it was sent (#277): its text, live, in a nested
 * editor, with an icon in the margin that opens the excerpt menu and a quiet
 * line naming where it came from. Edits made here are written to the source
 * (ExcerptStore.applyEdit) - through the source editor when it is open in
 * this tab, otherwise as a revision-checked save of the source.
 *
 * Shows nothing unless the excerpt is live and this document is still one of
 * its destinations.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { NodeViewWrapper, useEditor, EditorContent, type NodeViewProps } from '@tiptap/react';
import type { JSONContent } from '@tiptap/core';
import { useNavigate } from 'react-router-dom';
import { ExcerptKindIcon } from '../excerpts/ExcerptKindIcon';
import { useExcerptStore, useExcerptStoreVersion } from '../../contexts/ExcerptSyncContext';
import { useToast } from '../../contexts/ToastContext';
import { dossierExtensions } from './editorExtensions';
import { createMentionSuggestion } from './MentionExtension';
import { createFollowUpExtension } from './FollowUpExtension';
import { ExcerptMenu } from '../excerpts/ExcerptMenu';
import { itemUrl, sourceUrl } from '../excerpts/excerptActions';

const SAVE_DELAY_MS = 700;

let nestedExtensionsCache: ReturnType<typeof dossierExtensions> | null = null;
function nestedExtensions() {
  nestedExtensionsCache ??= dossierExtensions({
    placeholder: '',
    handleImageError: () => {},
    mentionSuggestion: createMentionSuggestion({ onMentionInserted: () => {} }),
    onTaskStatusChange: undefined,
    followUpExtension: createFollowUpExtension({ onUndo: () => {}, onTextChanged: () => {} }),
    // An embed shows part of a document: a filed item's line is usually not in
    // it, so mirroring would delete the item from the source (#337 review #2).
    lists: { mirror: false },
  });
  return nestedExtensionsCache;
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export function ExcerptEmbedView({ node, editor, selected }: NodeViewProps) {
  const id = node.attrs.excerptId as string;
  const store = useExcerptStore();
  useExcerptStoreVersion(store);
  const navigate = useNavigate();
  const host = editor.storage.excerptEmbed?.host ?? null;
  const depth = editor.storage.excerptEmbed?.depth ?? 0;
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const view = store && id ? store.view(id) : null;
  const shown = !!view && view.live
    && (!host || view.destinations.some((d) => d.type === host.type && d.id === host.id));

  if (!shown || !store) {
    return <NodeViewWrapper as="div" className="excerpt-embed-hidden" data-excerpt-id={id} hidden />;
  }

  if (depth > 0) {
    // An embed inside an embed: a link, not another editor.
    return (
      <NodeViewWrapper as="div" className="excerpt-embed-nested" data-excerpt-id={id}>
        <button type="button" className="excerpt-source-link" onClick={() => navigate(sourceUrl(view))}>
          <ExcerptKindIcon kind={view.kind} linkedId={view.linked_entity_id} size={12} /> {view.plain_text?.slice(0, 80) || 'Excerpt'}
        </button>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper as="div" className={`excerpt-embed excerpt-embed-${view.kind}${selected ? ' is-selected' : ''}`} data-excerpt-id={id}>
      <button
        type="button"
        contentEditable={false}
        className={`excerpt-embed-icon excerpt-icon excerpt-icon-${view.kind}`}
        aria-label="Excerpt menu"
        data-drag-handle
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => setMenuAnchor(e.currentTarget)}
      />
      <NestedExcerpt id={id} editable={editor.isEditable} />
      <div className="excerpt-source-line" contentEditable={false}>
        from{' '}
        <button type="button" onClick={() => navigate(sourceUrl(view))}>{view.source.title}</button>
        {view.linked_entity_id && view.kind !== 'excerpt' && host?.id !== view.linked_entity_id && (
          <>
            {' - '}
            <button type="button" onClick={() => navigate(itemUrl(view.kind, view.linked_entity_id!))}>open {view.kind}</button>
          </>
        )}
      </div>
      {menuAnchor && (
        <ExcerptMenu ids={[id]} anchor={menuAnchor} where="destination" host={host ?? undefined} onClose={() => setMenuAnchor(null)} />
      )}
    </NodeViewWrapper>
  );
}

function NestedExcerpt({ id, editable }: { id: string; editable: boolean }) {
  const store = useExcerptStore()!;
  const version = useExcerptStoreVersion(store);
  const toast = useToast();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initial = useMemo(() => store.fragment(id), []); // eslint-disable-line react-hooks/exhaustive-deps
  const schemaRef = useRef<import('@tiptap/pm/model').Schema | null>(null);

  const save = (content: JSONContent) => {
    if (!schemaRef.current) return;
    store.applyEdit(id, content, schemaRef.current).catch(() => {
      toast.error("Couldn't save the change to the excerpt");
      store.invalidate([id]);
    });
  };

  const nested = useEditor({
    extensions: nestedExtensions(),
    content: initial ?? undefined,
    editable,
    onCreate: ({ editor: ed }) => {
      ed.storage.excerptEmbed.depth = 1;
      schemaRef.current = ed.schema;
    },
    onUpdate: ({ editor: ed }) => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => save(ed.getJSON()), SAVE_DELAY_MS);
    },
    onBlur: ({ editor: ed }) => {
      if (!timer.current) return;
      clearTimeout(timer.current);
      timer.current = null;
      save(ed.getJSON());
    },
    editorProps: { attributes: { class: 'tiptap excerpt-embed-body' } },
  });

  // Follow the source while not being edited here.
  useEffect(() => {
    if (!nested || nested.isDestroyed || nested.isFocused || timer.current) return;
    const frag = store.fragment(id);
    if (frag && !same(frag, nested.getJSON())) nested.commands.setContent(frag, { emitUpdate: false });
  }, [version, nested, store, id]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  return <EditorContent editor={nested} />;
}
